/**
 * SettingsGeneralPage — Theme, Accent Color & Language preferences
 */

import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Sun, Moon, Monitor, Check, Loader2, RotateCcw, Save } from 'lucide-react';
import { useAppStore, type AccentColor } from '@/stores/app-store';
import type { Language } from '@/stores/app-store';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { useLayoutConfigContext } from '@/lib/use-layout-config';
import type { NavigationCategory, SurfaceNavigationConfig } from '@/lib/use-layout-config';
import { isPwaVoiceCommandsEnabled, setPwaVoiceCommandsEnabled } from '@/lib/voice-command-preferences';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { LayoutSwitcher } from '@/components/LayoutSwitcher';
import { VISUAL_PATTERN_OPTIONS, type VisualPatternId } from '@/lib/portal-theme';

type Theme = 'light' | 'dark' | 'system';

const THEME_OPTIONS: Array<{ value: Theme; icon: typeof Sun; labelKey: string; fallback: string }> = [
  { value: 'light',  icon: Sun,     labelKey: 'theme.light',  fallback: 'Light' },
  { value: 'dark',   icon: Moon,    labelKey: 'theme.dark',   fallback: 'Dark' },
  { value: 'system', icon: Monitor, labelKey: 'theme.system', fallback: 'System' },
];

const ACCENT_OPTIONS: Array<{ id: AccentColor; label: string; swatch: string }> = [
  { id: 'blue',   label: 'Blue',   swatch: 'bg-blue-500' },
  { id: 'rose',   label: 'Rose',   swatch: 'bg-rose-400' },
  { id: 'pink',   label: 'Pink',   swatch: 'bg-pink-400' },
  { id: 'purple', label: 'Purple', swatch: 'bg-purple-500' },
  { id: 'teal',   label: 'Teal',   swatch: 'bg-teal-500' },
  { id: 'green',  label: 'Green',  swatch: 'bg-green-500' },
  { id: 'amber',  label: 'Amber',  swatch: 'bg-amber-500' },
  { id: 'indigo', label: 'Indigo', swatch: 'bg-indigo-500' },
];

const LANG_OPTIONS: Array<{ value: Language; flag: string; label: string }> = [
  { value: 'en', flag: '🇬🇧', label: 'English' },
  { value: 'zh', flag: '🇨🇳', label: '中文 (简体)' },
  { value: 'zh-Hant', flag: '🇹🇼', label: '繁體中文' },
  { value: 'id', flag: '🇮🇩', label: 'Bahasa Indonesia' },
  { value: 'ms', flag: '🇲🇾', label: 'Bahasa Melayu' },
  { value: 'ja', flag: '🇯🇵', label: '日本語' },
  { value: 'th', flag: '🇹🇭', label: 'ไทย' },
  { value: 'vi', flag: '🇻🇳', label: 'Tiếng Việt' },
];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border bg-card p-4 space-y-3">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h2>
      {children}
    </section>
  );
}

interface NavConfigRecord {
  key: string;
  value: {
    pwaNavigation?: SurfaceNavigationConfig;
    extensionNavigation?: SurfaceNavigationConfig;
  } & Record<string, unknown>;
}

export default function SettingsGeneralPage() {
  const { t } = useTranslation();
  const { theme, setTheme, language, setLanguage, accent, setAccent, pwaVisualPattern, setPwaVisualPattern } = useAppStore();
  const { user } = useAuth();
  const { config, refresh } = useLayoutConfigContext();

  const [loadingSurfaceMenus, setLoadingSurfaceMenus] = useState(false);
  const [savingSurfaceMenus, setSavingSurfaceMenus] = useState(false);
  const [tenantSurfaceConfig, setTenantSurfaceConfig] = useState<NavConfigRecord['value'] | null>(null);
  const [userSurfaceConfig, setUserSurfaceConfig] = useState<NavConfigRecord['value'] | null>(null);
  const [selectedPwaIds, setSelectedPwaIds] = useState<string[]>([]);
  const [selectedExtensionIds, setSelectedExtensionIds] = useState<string[]>([]);
  const [pwaVoiceCommandsEnabled, setPwaVoiceCommandsEnabledState] = useState<boolean>(() => isPwaVoiceCommandsEnabled());

  const tenantConfigKey = user?.tenantId ? `nav:tenant:${user.tenantId}:config` : null;
  const userConfigKey = user?.id ? `nav:user:${user.id}:config` : null;

  const availableSurfaceCategories = useMemo(() => {
    const map = new Map<string, NavigationCategory>();
    const allSources = [
      ...(config.navigation ?? []),
      ...(tenantSurfaceConfig?.pwaNavigation?.navigation ?? []),
      ...(tenantSurfaceConfig?.extensionNavigation?.navigation ?? []),
      ...(userSurfaceConfig?.pwaNavigation?.navigation ?? []),
      ...(userSurfaceConfig?.extensionNavigation?.navigation ?? []),
    ];
    for (const cat of allSources) {
      if (!map.has(cat.id)) map.set(cat.id, cat);
    }
    return Array.from(map.values()).sort((a, b) => a.order - b.order);
  }, [config.navigation, tenantSurfaceConfig, userSurfaceConfig]);

  const hydrateSelections = useCallback((tenantValue: NavConfigRecord['value'] | null, userValue: NavConfigRecord['value'] | null) => {
    setSelectedPwaIds((userValue?.pwaNavigation?.navigation ?? tenantValue?.pwaNavigation?.navigation ?? []).map((cat) => cat.id));
    setSelectedExtensionIds((userValue?.extensionNavigation?.navigation ?? tenantValue?.extensionNavigation?.navigation ?? []).map((cat) => cat.id));
  }, []);

  useEffect(() => {
    if (!tenantConfigKey || !userConfigKey) return;
    let cancelled = false;
    setLoadingSurfaceMenus(true);

    Promise.all([
      api.get<NavConfigRecord>(`/domain-configs/${encodeURIComponent(tenantConfigKey)}`).catch(() => null),
      api.get<NavConfigRecord>(`/domain-configs/${encodeURIComponent(userConfigKey)}`).catch(() => null),
    ]).then(([tenantRecord, userRecord]) => {
      if (cancelled) return;
      const tenantValue = tenantRecord?.value ?? null;
      const userValue = userRecord?.value ?? null;
      setTenantSurfaceConfig(tenantValue);
      setUserSurfaceConfig(userValue);
      hydrateSelections(tenantValue, userValue);
    }).finally(() => {
      if (!cancelled) setLoadingSurfaceMenus(false);
    });

    return () => {
      cancelled = true;
    };
  }, [hydrateSelections, tenantConfigKey, userConfigKey]);

  function toggleSelection(
    current: string[],
    setCurrent: Dispatch<SetStateAction<string[]>>,
    id: string,
  ) {
    setCurrent((prev) => prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]);
  }

  async function saveSurfaceMenus() {
    if (!userConfigKey) return;
    setSavingSurfaceMenus(true);
    try {
      const sourceMap = new Map(availableSurfaceCategories.map((cat) => [cat.id, cat]));
      const pwaNavigation = selectedPwaIds
        .map((id) => sourceMap.get(id))
        .filter((cat): cat is NavigationCategory => Boolean(cat));
      const extensionNavigation = selectedExtensionIds
        .map((id) => sourceMap.get(id))
        .filter((cat): cat is NavigationCategory => Boolean(cat));

      const payload: NavConfigRecord['value'] = {
        ...(userSurfaceConfig ?? {}),
        pwaNavigation: {
          navigation: pwaNavigation,
          hideExtraActions: userSurfaceConfig?.pwaNavigation?.hideExtraActions
            ?? tenantSurfaceConfig?.pwaNavigation?.hideExtraActions
            ?? config.pwaNavigation?.hideExtraActions,
        },
        extensionNavigation: {
          navigation: extensionNavigation,
          hideExtraActions: userSurfaceConfig?.extensionNavigation?.hideExtraActions
            ?? tenantSurfaceConfig?.extensionNavigation?.hideExtraActions
            ?? config.extensionNavigation?.hideExtraActions,
        },
      };

      await api.put(`/domain-configs/${encodeURIComponent(userConfigKey)}`, payload);
      setUserSurfaceConfig(payload);
      refresh();
      toast.success(t('settings.surfaceSaved', 'Personal surface menu saved'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('settings.surfaceSaveFailed', 'Failed to save personal menu'));
    } finally {
      setSavingSurfaceMenus(false);
    }
  }

  async function resetSurfaceMenus() {
    if (!userConfigKey) return;
    setSavingSurfaceMenus(true);
    try {
      await api.delete(`/domain-configs/${encodeURIComponent(userConfigKey)}`).catch(() => undefined);
      setUserSurfaceConfig(null);
      hydrateSelections(tenantSurfaceConfig, null);
      refresh();
      toast.success(t('settings.surfaceReset', 'Personal surface menu reset'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('settings.surfaceResetFailed', 'Failed to reset personal menu'));
    } finally {
      setSavingSurfaceMenus(false);
    }
  }

  const handlePwaVoiceCommandsChange = useCallback((checked: boolean) => {
    setPwaVoiceCommandsEnabledState(checked);
    setPwaVoiceCommandsEnabled(checked);
    toast.success(
      checked
        ? t('settings.voiceCommandsEnabled', 'PWA voice commands enabled')
        : t('settings.voiceCommandsDisabled', 'PWA voice commands disabled'),
    );
  }, [t]);

  const [clearTabsOnSignout, setClearTabsOnSignout] = useState<boolean>(() => {
    return localStorage.getItem('boox_clear_tabs_on_signout') !== 'false';
  });

  const handleClearTabsChange = useCallback((checked: boolean) => {
    setClearTabsOnSignout(checked);
    localStorage.setItem('boox_clear_tabs_on_signout', String(checked));
    toast.success(
      checked
        ? t('settings.clearTabsEnabled', 'Tabs will be cleared on sign out')
        : t('settings.clearTabsDisabled', 'Tabs will be preserved across sessions'),
    );
  }, [t]);

  return (
    <div className="mx-auto max-w-lg space-y-4 py-6 px-4">
      <div className="pb-1">
        <h1 className="text-xl font-bold">{t('user.settings', 'Settings')}</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {t('settings.desc', 'Personalise your Boox experience.')}
        </p>
      </div>

      <Section title={t('theme.label', 'Theme')}>
        <div className="grid grid-cols-3 gap-2">
          {THEME_OPTIONS.map(({ value, icon: Icon, labelKey, fallback }) => {
            const active = theme === value;
            return (
              <button
                key={value}
                onClick={() => setTheme(value)}
                className={cn(
                  'relative flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-all cursor-pointer',
                  active
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{t(labelKey, fallback)}</span>
                {active && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Check className="h-2.5 w-2.5" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </Section>

      <Section title={t('accent.label', 'Accent Color')}>
        <div className="flex flex-wrap gap-3 px-1">
          {ACCENT_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setAccent(opt.id)}
              title={opt.label}
              className={cn(
                'relative flex h-9 w-9 items-center justify-center rounded-full ring-2 ring-offset-2 transition-all cursor-pointer',
                opt.swatch,
                accent === opt.id
                  ? 'ring-primary scale-110'
                  : 'ring-transparent hover:ring-primary/40 hover:scale-105',
              )}
            >
              {accent === opt.id && <Check className="h-4 w-4 text-white drop-shadow" />}
            </button>
          ))}
        </div>
      </Section>

      <Section title="PWA Background Pattern">
        <select
          value={pwaVisualPattern}
          onChange={(event) => setPwaVisualPattern(event.target.value as VisualPatternId)}
          className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer"
        >
          {VISUAL_PATTERN_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground px-0.5">
          Used by the PWA sidebar menu panel. Company Settings still controls login and tenant branding.
        </p>
      </Section>

      <Section title={t('theme.languageLabel', 'Language')}>
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value as Language)}
          className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer"
        >
          {LANG_OPTIONS.map(({ value, flag, label }) => (
            <option key={value} value={value}>
              {flag}  {label}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground px-0.5">
          {t('settings.langHint', 'Applies immediately across the whole app.')}
        </p>
      </Section>

      <Section title={t('settings.layoutExperience', 'Layout Experience')}>
        <LayoutSwitcher variant="settings-row" />
        <p className="text-xs text-muted-foreground px-0.5">
          {t('settings.layoutHint', 'Switch instantly — no reload needed. Classic mode uses a compact 2-column view that mirrors the legacy system. Modern mode enables panels, right dock, and column sets.')}
        </p>
      </Section>

      <Section title={t('settings.voiceCommands', 'Voice Commands')}>
        <div className="flex items-center justify-between gap-3 rounded-lg border bg-background px-3 py-3">
          <div className="space-y-1">
            <div className="text-sm font-medium">
              {t('settings.voiceCommandsAlwaysOn', 'Always-on PWA voice commands')}
            </div>
            <div className="text-xs text-muted-foreground">
              {t('settings.voiceCommandsAlwaysOnDesc', 'Adds a mic toggle in the PWA sidebar and keeps listening after each command until you turn it off.')}
            </div>
          </div>
          <Switch
            checked={pwaVoiceCommandsEnabled}
            onCheckedChange={handlePwaVoiceCommandsChange}
            aria-label={t('settings.voiceCommandsAlwaysOn', 'Always-on PWA voice commands')}
          />
        </div>
      </Section>

      <Section title={t('settings.tabs', 'Tabs')}>
        <div className="flex items-center justify-between gap-3 rounded-lg border bg-background px-3 py-3">
          <div className="space-y-1">
            <div className="text-sm font-medium">
              {t('settings.clearTabsOnSignout', 'Clear tabs on sign out')}
            </div>
            <div className="text-xs text-muted-foreground">
              {t('settings.clearTabsOnSignoutDesc', 'When enabled, all open tabs are closed when you sign out. Disable to keep your tabs for next session.')}
            </div>
          </div>
          <Switch
            checked={clearTabsOnSignout}
            onCheckedChange={handleClearTabsChange}
            aria-label={t('settings.clearTabsOnSignout', 'Clear tabs on sign out')}
          />
        </div>
      </Section>

      <Section title={t('settings.surfaceMenu', 'Personal Surface Menu')}>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-muted/20 px-3 py-2">
            <div className="text-xs text-muted-foreground">
              {t('settings.surfaceMenuDesc', 'Choose which menu categories appear for you in PWA and Chrome extension.')}
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => resetSurfaceMenus()} disabled={savingSurfaceMenus || loadingSurfaceMenus}>
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                {t('common.reset', 'Reset')}
              </Button>
              <Button type="button" size="sm" onClick={() => void saveSurfaceMenus()} disabled={savingSurfaceMenus || loadingSurfaceMenus}>
                {savingSurfaceMenus ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
                {t('common.save', 'Save')}
              </Button>
            </div>
          </div>

          <div className="rounded-lg border bg-background px-3 py-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">{t('settings.pwaMenu', 'PWA Menu')}</div>
                <div className="text-xs text-muted-foreground">
                  {t('settings.pwaMenuDesc', 'Current personal menu applied in go-pwa.')}
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                {selectedPwaIds.length} {t('settings.categories', 'categories')}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {loadingSurfaceMenus ? (
                <span className="inline-flex items-center text-xs text-muted-foreground">
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  {t('common.loading', 'Loading…')}
                </span>
              ) : availableSurfaceCategories.map((cat) => {
                const active = selectedPwaIds.includes(cat.id);
                return (
                  <button
                    key={`pwa-${cat.id}`}
                    type="button"
                    onClick={() => toggleSelection(selectedPwaIds, setSelectedPwaIds, cat.id)}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs transition-colors',
                      active ? 'border-primary bg-primary/10 text-primary' : 'bg-muted/20 text-muted-foreground hover:border-primary/40 hover:text-foreground',
                    )}
                  >
                    {cat.labelKey ? t(cat.labelKey, cat.label) : t(`category.${cat.id}`, cat.label)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-lg border bg-background px-3 py-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">{t('settings.extensionMenu', 'Extension Menu')}</div>
                <div className="text-xs text-muted-foreground">
                  {t('settings.extensionMenuDesc', 'Current personal menu applied in Chrome extension.')}
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                {selectedExtensionIds.length} {t('settings.categories', 'categories')}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {loadingSurfaceMenus ? (
                <span className="inline-flex items-center text-xs text-muted-foreground">
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  {t('common.loading', 'Loading…')}
                </span>
              ) : availableSurfaceCategories.map((cat) => {
                const active = selectedExtensionIds.includes(cat.id);
                return (
                  <button
                    key={`ext-${cat.id}`}
                    type="button"
                    onClick={() => toggleSelection(selectedExtensionIds, setSelectedExtensionIds, cat.id)}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs transition-colors',
                      active ? 'border-primary bg-primary/10 text-primary' : 'bg-muted/20 text-muted-foreground hover:border-primary/40 hover:text-foreground',
                    )}
                  >
                    {cat.labelKey ? t(cat.labelKey, cat.label) : t(`category.${cat.id}`, cat.label)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
